import React, { createContext, useContext, useState, useEffect, useRef } from 'react'
import { api, ApiError } from '../utils/api'

// Exported for consumers that must not throw outside the provider (a page
// rendered in isolation, as in tests): read it with useContext and treat null
// as "no user". Everything else uses useAuth().
export const AuthContext = createContext(null)

// Module-to-permission mapping for modules that don't map 1:1
// Nav modules that ride an existing permission rather than one of their own.
// A module id with no matching permission resolves to a key nobody has been
// granted, which hides the menu from everyone except admins.
export const MODULE_MAP = { channels: 'inventory', pricing: 'pricing:view' }

// Positive Deals capabilities (spec docs/archive/202609/specs_20260916_quote_review.md, D6).
// canFinalize drives the finalise buttons; isDealsReviewer drives the restricted
// reviewer UI. No !admin needed — canFinalize excludes admins from "reviewer".
// Pure + exported so the gating is unit-testable without the provider.
export function deriveCanFinalize(user, permissions = []) {
  if (!user) return false
  if (user.is_admin) return true
  return permissions.includes('deals:finalize')
}
export function deriveIsDealsReviewer(user, permissions = []) {
  if (!user) return false
  return permissions.includes('deals:review') && !deriveCanFinalize(user, permissions)
}

/**
 * Build navigation array from flat permission list.
 * Replaces the server-side getAccessibleNavigation().
 */
function buildNavigation(permissions, isAdmin) {
  const has = (key) => isAdmin || permissions.includes(key)
  const nav = []

  if (has('tickets')) {
    nav.push({ id: 'tickets', name: 'Tickets', path: '/tickets' })
  }

  if (has('deals')) {
    nav.push({
      id: 'deals',
      name: 'Deals',
      path: '/deals',
      children: [
        { id: 'quote-requested', name: 'Quote Requested', path: '/deals/quote-requested' },
        { id: 'all-deals', name: 'All Deals', path: '/deals/all' }
      ]
    })
  }

  if (has('inventory')) {
    nav.push({
      id: 'inventory',
      name: 'Inventory',
      path: '/inventory',
      children: [
        { id: 'drafts', name: 'Drafts Manager', path: '/inventory/drafts' },
        { id: 'inventory-list', name: 'Inventory', path: '/inventory/list' }
      ]
    })
  }

  if (has('pricing:view')) {
    nav.push({
      id: 'pricing',
      name: 'Pricing',
      path: '/pricing',
      children: [
        { id: 'pricing-management', name: 'Pricing Management', path: '/pricing/management' },
        { id: 'pricing-rules', name: 'Pricing Rules', path: '/pricing/rules' },
        { id: 'pricing-suggestions', name: 'Pricing Suggestions', path: '/pricing/suggestions' },
        { id: 'pricing-audit-log', name: 'Audit Log', path: '/pricing/audit-log' }
      ]
    })
  }

  if (has('orders')) {
    nav.push({ id: 'orders', name: 'Orders', path: '/orders' })
  }

  if (has('warehouse')) {
    nav.push({
      id: 'warehouse',
      name: 'Warehouse',
      path: '/warehouse',
      children: [
        { id: 'receiving', name: 'Receiving', path: '/warehouse/receiving' },
        { id: 'testing', name: 'Testing', path: '/warehouse/testing' },
        { id: 'repairs', name: 'Repairs', path: '/warehouse/repairs' },
        { id: 'issue-trades', name: 'Issue Trades', path: '/warehouse/issue-trades' },
        { id: 'to-send-back', name: 'To Send Back', path: '/warehouse/to-send-back' },
        { id: 'photography', name: 'Photography', path: '/warehouse/photography' },
        { id: 'putaway', name: 'Putaway', path: '/warehouse/putaway' },
        { id: 'collections', name: 'Collections', path: '/warehouse/collections' },
        { id: 'shipping', name: 'Order Shipping', path: '/warehouse/shipping' }
      ]
    })
  }

  if (has('pim')) {
    nav.push({
      id: 'pim',
      name: 'PIM',
      path: '/pim',
      children: [
        { id: 'pim-products', name: 'Products', path: '/pim/products' },
        { id: 'pim-brands', name: 'Brands', path: '/pim/brands' },
        { id: 'pim-categories', name: 'Categories', path: '/pim/categories' },
        { id: 'pim-dimensions', name: 'Dimensions', path: '/pim/dimensions' }
      ]
    })
  }

  if (isAdmin) {
    nav.push({
      id: 'admin',
      name: 'Admin',
      path: '/admin',
      children: [
        { id: 'users', name: 'User Management', path: '/admin/users' }
      ]
    })
  }

  return nav
}

/**
 * Apply user context from a successful auth response.
 */
function applyUserContext(data) {
  const user = data.user || data
  return {
    user: {
      user_id: user.user_id,
      employee_id: user.employee_id,
      name: user.name,
      is_admin: user.is_admin
    },
    permissions: user.permissions || [],
    roles: user.roles || [],
    navigation: buildNavigation(user.permissions || [], user.is_admin)
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [permissions, setPermissions] = useState([])
  const [roles, setRoles] = useState([])
  const [navigation, setNavigation] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [connectionError, setConnectionError] = useState(false)
  const retryTimeoutRef = useRef(null)

  useEffect(() => {
    checkAuth()
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current)
      }
    }
  }, [])

  async function checkAuth() {
    try {
      const response = await api.get('/api/auth/verify')
      if (response.success) {
        const ctx = applyUserContext(response)
        setUser(ctx.user)
        setPermissions(ctx.permissions)
        setRoles(ctx.roles)
        setNavigation(ctx.navigation)
        setConnectionError(false)
      }
    } catch (err) {
      // Distinguish between auth errors (401/403) and transient errors (5xx)
      if (err instanceof ApiError && err.isRetryable) {
        // Transient error (502, 503, etc.) — don't logout, show connection error
        // Keep existing user state if we have it (page refresh during deploy)
        setConnectionError(true)
        // Auto-retry after 5 seconds
        retryTimeoutRef.current = setTimeout(() => {
          checkAuth()
        }, 5000)
      } else {
        // Auth error (401, 403) or no session — clear state
        setUser(null)
        setPermissions([])
        setRoles([])
        setNavigation([])
        setConnectionError(false)
      }
    } finally {
      setLoading(false)
    }
  }

  async function login(email, password) {
    setError(null)
    try {
      const response = await api.post('/api/auth/login', { email, password })
      if (response.success) {
        const ctx = applyUserContext(response)
        setUser(ctx.user)
        setPermissions(ctx.permissions)
        setRoles(ctx.roles)
        setNavigation(ctx.navigation)
        return { success: true }
      } else {
        setError(response.error || 'Login failed')
        return { success: false, error: response.error }
      }
    } catch (err) {
      const errorMsg = err.message || 'Login failed'
      setError(errorMsg)
      return { success: false, error: errorMsg }
    }
  }

  async function loginEmployee(employeeId, pin) {
    setError(null)
    try {
      const response = await api.post('/api/auth/login/employee', {
        employee_id: employeeId,
        pin
      })
      if (response.success) {
        const ctx = applyUserContext(response)
        setUser(ctx.user)
        setPermissions(ctx.permissions)
        setRoles(ctx.roles)
        setNavigation(ctx.navigation)
        return { success: true }
      } else {
        setError(response.error || 'Login failed')
        return { success: false, error: response.error }
      }
    } catch (err) {
      const errorMsg = err.message || 'Login failed'
      setError(errorMsg)
      return { success: false, error: errorMsg }
    }
  }

  async function logout() {
    try {
      await api.delete('/api/auth/session')
    } catch (err) {
      // Ignore logout errors
    }
    setUser(null)
    setPermissions([])
    setRoles([])
    setNavigation([])
  }

  function hasPermission(key) {
    if (!user) return false
    if (user.is_admin) return true
    return permissions.includes(key)
  }

  function hasModule(module) {
    const key = MODULE_MAP[module] || module
    return hasPermission(key)
  }

  const canFinalize = deriveCanFinalize(user, permissions)
  const isDealsReviewer = deriveIsDealsReviewer(user, permissions)

  return (
    <AuthContext.Provider value={{
      user,
      permissions,
      roles,
      navigation,
      loading,
      error,
      connectionError,
      login,
      loginEmployee,
      logout,
      hasModule,
      hasPermission,
      canFinalize,
      isDealsReviewer,
      checkAuth
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
